import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Table from '../../components/common/Table';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon  } from '@heroicons/react/24/outline';

/* -----------------------------
 * Small local debounce utility
 * ---------------------------*/
function useDebouncedValue(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const SEGMENTS = {
  ACTIVE: 'active',
  DELETED: 'deleted',
  ALL: 'all',
};

export default function EnrollmentsAdmin() {
  const queryClient = useQueryClient();

  const [segment, setSegment] = useState(SEGMENTS.DELETED);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Drawer (Details)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerTab, setDrawerTab] = useState('summary'); // 'summary' | 'payments'

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Reset selections when segment/search changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [segment, debouncedSearch]);

  /* -----------------------------
   * Counts (Active / Deleted)
   * ---------------------------*/
  const { data: activeCount = 0 } = useQuery({
    queryKey: ['admin_enrollments_count', 'active'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('enrollments')
        .select('id', { count: 'exact', head: true })
        .eq('is_deleted', false);
      if (error) {
        console.error('Active count error', error);
        return 0;
      }
      return count || 0;
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { data: deletedCount = 0 } = useQuery({
    queryKey: ['admin_enrollments_count', 'deleted'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('enrollments')
        .select('id', { count: 'exact', head: true })
        .eq('is_deleted', true);
      if (error) {
        console.error('Deleted count error', error);
        return 0;
      }
      return count || 0;
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  /* -----------------------------
   * Shared list query builder (used by UI + Export)
   * ---------------------------*/
  const buildListQuery = (opts) => {
    const { seg, termLike } = opts;

    const studentsSelect = termLike
      ? 'students!inner ( id, full_name, nric_passport )' // narrow when searching
      : 'students ( id, full_name, nric_passport )';

    const baseSelect = `
      id, student_id, subject_id, intake_id, enrollment_date, status, is_deleted,
      ${studentsSelect},
      subjects:subjects ( id, code, name ),
      intakes:intakes ( id, name, start_date, end_date )
    `;

    const segFilter = (q) => {
      if (seg === SEGMENTS.ACTIVE) return q.eq('is_deleted', false);
      if (seg === SEGMENTS.DELETED) return q.eq('is_deleted', true);
      return q;
    };

    let q = supabase.from('enrollments').select(baseSelect);
    q = segFilter(q);

    if (termLike) {
      // IMPORTANT: filter nested column with dot-notation
      q = q.ilike('students.full_name', termLike);
    }

    return q.order('enrollment_date', { ascending: false });
  };

  /* -----------------------------
   * List loader (Segment + Student-Name-only Search)
   * ---------------------------*/
  const {
    data: rows = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin_enrollments_list', segment, debouncedSearch],
    queryFn: async () => {
      const term = debouncedSearch?.trim();
      const like = term ? `%${term}%` : null;

      const listQ = buildListQuery({ seg: segment, termLike: like });
      const { data, error } = await listQ;
      if (error) {
        console.error('EnrollmentsAdmin list query error', error);
        return [];
      }

      if (!data || data.length === 0) return [];
      const ids = data.map((r) => String(r.id));
      const { data: peRows, error: peErr } = await supabase
        .from('payment_enrollments')
        .select('enrollment_id')
        .in('enrollment_id', ids);
      if (peErr) console.error('payment_enrollments fetch error', peErr);

      const counts = new Map();
      (peRows || []).forEach((r) => {
        const key = String(r.enrollment_id);
        counts.set(key, (counts.get(key) || 0) + 1);
      });

      return data.map((r) => ({
        ...r,
        _linkedPaymentsCount: counts.get(String(r.id)) || 0,
      }));
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  const data = rows;

  /* -----------------------------
   * Bulk selection helpers
   * ---------------------------*/
  const allVisibleIds = useMemo(() => data.map((r) => String(r.id)), [data]);
  const allSelectedOnPage = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const anySelected = selectedIds.size > 0;

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      return copy;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (allSelectedOnPage) {
        allVisibleIds.forEach((id) => copy.delete(id));
      } else {
        allVisibleIds.forEach((id) => copy.add(id));
      }
      return copy;
    });
  };

  const clearSelections = () => setSelectedIds(new Set());
  const clearSearch = () => setSearch('');

  /* -----------------------------
   * Mutations: Restore, Hard Delete
   * ---------------------------*/
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin_enrollments_list'] });
    queryClient.invalidateQueries({ queryKey: ['admin_enrollments_count', 'active'] });
    queryClient.invalidateQueries({ queryKey: ['admin_enrollments_count', 'deleted'] });
  };

  const restoreMutation = useMutation({
    mutationFn: async (ids) => {
      const { error } = await supabase
        .from('enrollments')
        .update({ is_deleted: false })
        .in('id', ids);
      if (error) throw error;
      return { success: true, ids };
    },
    onSuccess: () => {
      invalidateAll();
      alert('Enrollment(s) restored.');
    },
    onError: (e) => {
      console.error(e);
      alert('Failed to restore enrollment(s).');
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      const { error: unlinkErr } = await supabase
        .from('payment_enrollments')
        .delete()
        .in('enrollment_id', ids);
      if (unlinkErr) throw unlinkErr;

      const { error: delErr } = await supabase
        .from('enrollments')
        .delete()
        .in('id', ids);
      if (delErr) throw delErr;

      return { success: true, ids };
    },
    onSuccess: () => {
      invalidateAll();
      alert('Enrollment(s) permanently deleted.');
    },
    onError: (e) => {
      console.error(e);
      alert('Failed to hard delete enrollment(s). They may still be linked or constrained.');
    },
  });

  /* -----------------------------
   * Row actions
   * ---------------------------*/
  const openDetails = (row) => {
    setDrawerRow(row);
    setDrawerTab('summary');
    setDrawerOpen(true);
  };

  const onRestore = async (row) => {
    if (!row?.is_deleted) return;
    const ok = window.confirm('Restore this enrollment?');
    if (!ok) return;
    restoreMutation.mutate([String(row.id)]);
  };

  const onHardDelete = async (row) => {
    if (!row?.is_deleted) return;
    const msg =
      `Hard delete this enrollment?\n\n` +
      `This will first remove any links in payment_enrollments, then permanently delete the enrollment.`;
    const ok = window.confirm(msg);
    if (!ok) return;
    hardDeleteMutation.mutate([String(row.id)]);
  };

  const onBulkRestore = () => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(`Restore ${selectedIds.size} selected enrollment(s)?`);
    if (!ok) return;
    restoreMutation.mutate(Array.from(selectedIds));
  };

  const onBulkHardDelete = async () => {
    if (selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    const { data: peRows, error: e } = await supabase
      .from('payment_enrollments')
      .select('enrollment_id')
      .in('enrollment_id', ids);
    if (e) console.error(e);

    const counts = new Map();
    (peRows || []).forEach((r) => {
      const key = String(r.enrollment_id);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const totalLinks = Array.from(counts.values()).reduce((a, b) => a + b, 0);

    const ok = window.confirm(
      `Hard delete ${ids.length} enrollment(s)?\n\n` +
        (totalLinks > 0
          ? `We will remove ${totalLinks} payment link(s) first (payment_enrollments), then permanently delete the enrollments.`
          : `No payment links detected. Enrollments will be permanently deleted.`)
    );
    if (!ok) return;

    hardDeleteMutation.mutate(ids);
  };

  /* -----------------------------
   * Export CSV of current view (segment + search)
   * ---------------------------*/
  const exportCsv = async () => {
    try {
      const term = debouncedSearch?.trim();
      const like = term ? `%${term}%` : null;

      // 1) Fetch the same list as displayed
      const listQ = buildListQuery({ seg: segment, termLike: like });
      const { data: raw, error: listErr } = await listQ;
      if (listErr) throw listErr;

      // 2) Linked counts
      const ids = (raw || []).map((r) => String(r.id));
      let counts = new Map();
      if (ids.length > 0) {
        const { data: peRows, error: peErr } = await supabase
          .from('payment_enrollments')
          .select('enrollment_id')
          .in('enrollment_id', ids);
        if (peErr) throw peErr;

        counts = new Map();
        (peRows || []).forEach((r) => {
          const key = String(r.enrollment_id);
          counts.set(key, (counts.get(key) || 0) + 1);
        });
      }

      // 3) Build CSV
      const headers = [
        'Subject Code',
        'Subject Name',
        'Intake',
        'Student Name',
        'NRIC/Passport',
        'Enrollment Date',
        'Status',
        'Linked Payments (#)',
        'Deleted?',
      ];

      const escapeCsv = (val) => {
        if (val == null) return '';
        const s = String(val);
        if (s.includes('"') || s.includes(',') || s.includes('\n')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const lines = [headers.map(escapeCsv).join(',')];

      (raw || []).forEach((r) => {
        const row = [
          r?.subjects?.code ?? '',
          r?.subjects?.name ?? '',
          r?.intakes?.name ?? '',
          r?.students?.full_name ?? '',
          r?.students?.nric_passport ?? '',
          r?.enrollment_date ?? '',
          r?.status ?? '',
          counts.get(String(r.id)) || 0,
          r?.is_deleted ? 'Yes' : 'No',
        ];
        lines.push(row.map(escapeCsv).join(','));
      });

      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `enrollments-${segment}-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
      alert('Failed to export CSV.');
    }
  };

  /* -----------------------------
   * Columns (table)
   * ---------------------------*/
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={allSelectedOnPage}
            onChange={toggleSelectAllOnPage}
          />
        ),
        accessor: (row) => row.id,
        cell: (value) => (
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={selectedIds.has(String(value))}
            onChange={() => toggleSelectOne(String(value))}
          />
        ),
        width: '40px',
      },
      {
        header: 'Subject',
        accessor: (row) => row.subjects?.code || '',
        cell: (value, row) => (
          <div>
            <div className="font-medium">{row.subjects?.code || '-'}</div>
            <div className="text-sm text-gray-500">{row.subjects?.name || '-'}</div>
          </div>
        ),
      },
      {
        header: 'Intake',
        accessor: (row) => row.intakes?.name || '',
        cell: (value, row) => (
          <div>
            <div className="font-medium">{row.intakes?.name || '-'}</div>
            <div className="text-sm text-gray-500">
              {row.intakes?.start_date || '-'} to {row.intakes?.end_date || '-'}
            </div>
          </div>
        ),
      },
      {
        header: 'Student',
        accessor: (row) => row.students?.full_name || '',
        cell: (value, row) => (
          <div>
            <div className="font-medium">{row.students?.full_name || '-'}</div>
            <div className="text-sm text-gray-500">{row.students?.nric_passport || '-'}</div>
          </div>
        ),
      },
      {
        header: 'Enrolled',
        accessor: (row) => row.enrollment_date || '',
        cell: (value) => value || '-',
      },
      {
        header: 'Status',
        accessor: (row) => row.status || '',
        cell: (value) => (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              value === 'Active'
                ? 'bg-yellow-100 text-yellow-800'
                : value === 'Completed'
                ? 'bg-green-100 text-green-800'
                : value === 'Withdrawn'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value || '-'}
          </span>
        ),
      },
      {
        header: 'Linked Payments (#)',
        accessor: (row) => row._linkedPaymentsCount || 0,
        cell: (value) => <span className="font-medium">{value}</span>,
      },
      {
        header: 'Deleted?',
        accessor: (row) => row.is_deleted,
        cell: (value) => (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              value ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}
          >
            {value ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        header: 'Actions',
        accessor: (row) => row.id,
        cell: (value, row) => {
          const isDeleted = !!row.is_deleted;
          const restoring = restoreMutation.isPending;
          const deleting = hardDeleteMutation.isPending;
          return (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openDetails(row)}>
                Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!isDeleted || restoring}
                onClick={() => onRestore(row)}
              >
                {restoring ? 'Restoring…' : 'Restore'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600"
                disabled={!isDeleted || deleting}
                onClick={() => onHardDelete(row)}
              >
                {deleting ? 'Deleting…' : 'Hard Delete'}
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, allSelectedOnPage, restoreMutation.isPending, hardDeleteMutation.isPending]
  );

  /* -----------------------------
   * Linked Payments loader (for Drawer)
   * ---------------------------*/
  const {
    data: linkedPayments = [],
    isLoading: linkedLoading,
    error: linkedError,
  } = useQuery({
    queryKey: ['admin_enrollment_linked_payments', drawerRow?.id, drawerTab],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_enrollments')
        .select(`
          payment_id,
          enrollment_id,
          amount_applied,
          payments:payments (
            id, invoice_number, amount, payment_date, payment_method,
            payment_type, status, is_deleted
          )
        `)
        .eq('enrollment_id', drawerRow.id);
      if (error) {
        console.error('Linked payments fetch error', error);
        return [];
      }
      // Normalize shape
      return (data || []).map((r) => ({
        payment_id: r.payment_id,
        amount_applied: r.amount_applied,
        payment: r.payments || null,
      }));
    },
    enabled: drawerOpen && !!drawerRow?.id && drawerTab === 'payments',
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  /* -----------------------------
   * Render
   * ---------------------------*/
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
            <h1 className="text-xl font-bold text-gray-900">Enrollments Master Administration</h1>
            <p className="text-sm text-gray-500">Review, restore, permanently delete, or export enrollments.</p>
        </div>
        <div>
            {/* Export CSV (top-right), mirrors Payments Admin */}
            <Button
                variant="ghost"
                className="flex items-center gap-2"
                onClick={exportCsv}
                disabled={isLoading || (rows?.length ?? 0) === 0}
                title={rows.length ? 'Export current rows to CSV' : 'Nothing to export'}
            >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Export CSV
            </Button>
        </div>
      </div>

      {/* KPI Cards: Active / Deleted */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-semibold">{activeCount}</div>
            </div>
            <div className="text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Deleted</div>
              <div className="text-2xl font-semibold">{deletedCount}</div>
            </div>
            <div className="text-gray-600">
              <XCircleIcon className="h-8 w-8 text-rose-500 opacity-80" />
            </div>
          </div>
        </Card>
      </div>

      {/* Middle controls: segment toggle + (Payments-style) search */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Segmented toggle */}
          <div className="inline-flex rounded-md shadow-sm border overflow-hidden">
            {[
              { key: SEGMENTS.DELETED, label: 'Deleted' },
              { key: SEGMENTS.ACTIVE, label: 'Active' },
              { key: SEGMENTS.ALL, label: 'All' },
            ].map((opt, idx) => {
              const active = segment === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSegment(opt.key)}
                  className={`px-3 py-1.5 text-sm ${active ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} ${idx !== 0 ? 'border-l' : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Search input styled like Payments Admin: left icon + clear */}
            <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search student name"
                className="ml-3 flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
        </div>
      </Card>

      {/* Bottom bulk toolbar: selection + actions */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {anySelected ? `${selectedIds.size} selected` : ' '}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onBulkRestore}
              disabled={!anySelected || segment !== SEGMENTS.DELETED || restoreMutation.isPending}
            >
              {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600"
              onClick={onBulkHardDelete}
              disabled={!anySelected || segment !== SEGMENTS.DELETED || hardDeleteMutation.isPending}
            >
              {hardDeleteMutation.isPending ? 'Hard Deleting…' : 'Hard Delete'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { clearSelections(); clearSearch(); }}>
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card>
        {isLoading ? (
          <div className="text-center py-8">Loading enrollments…</div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">
            Error loading enrollments. Check console for details.
          </div>
        ) : (
          <Table
            columns={columns}
            data={data}
            emptyMessage={
              debouncedSearch
                ? 'No enrollments match your student search.'
                : segment === SEGMENTS.DELETED
                ? 'No deleted enrollments.'
                : 'No enrollments found.'
            }
          />
        )}
        {isFetching && (
          <div className="px-4 py-2 text-sm text-gray-400">Refreshing…</div>
        )}
      </Card>

      {/* Details Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-30"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Panel */}
          <div className="absolute inset-y-0 right-0 w-full sm:w-[520px] bg-white shadow-xl flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Enrollment Details</h2>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {drawerRow ? (
              <>
                {/* Tabs */}
                <div className="px-4 pt-3 border-b">
                  <nav className="-mb-px flex space-x-6">
                    {[
                      { key: 'summary', label: 'Summary' },
                      { key: 'payments', label: 'Linked Payments' },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setDrawerTab(tab.key)}
                        className={`whitespace-nowrap pb-3 border-b-2 text-sm font-medium ${
                          drawerTab === tab.key
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Content */}
                <div className="p-4 overflow-y-auto">
                  {drawerTab === 'summary' && (
                    <div className="space-y-3 text-sm">
                      <div>
                        <div className="text-gray-500">Subject</div>
                        <div className="font-medium">
                          {drawerRow.subjects?.code || '-'}{' '}
                          {drawerRow.subjects?.name ? `— ${drawerRow.subjects?.name}` : ''}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Intake</div>
                        <div className="font-medium">
                          {drawerRow.intakes?.name || '-'}{' '}
                          <span className="text-gray-500">
                            ({drawerRow.intakes?.start_date || '-'} to{' '}
                            {drawerRow.intakes?.end_date || '-'})
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Student</div>
                        <div className="font-medium">
                          {drawerRow.students?.full_name || '-'}
                        </div>
                        <div className="text-gray-500">
                          {drawerRow.students?.nric_passport || '-'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-gray-500">Enrollment Date</div>
                          <div className="font-medium">{drawerRow.enrollment_date || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Status</div>
                          <div className="font-medium">{drawerRow.status || '-'}</div>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Linked Payments (#)</div>
                        <div className="font-medium">
                          {drawerRow._linkedPaymentsCount ?? 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Deleted?</div>
                        <div className="font-medium">
                          {drawerRow.is_deleted ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </div>
                  )}

                  {drawerTab === 'payments' && (
                    <div className="space-y-3">
                      {linkedLoading ? (
                        <div className="text-sm text-gray-500">Loading linked payments…</div>
                      ) : linkedError ? (
                        <div className="text-sm text-red-600">Error loading linked payments.</div>
                      ) : linkedPayments.length === 0 ? (
                        <div className="text-sm text-gray-500">No payments linked to this enrollment.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Invoice
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Method
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Type
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Amount (Payment)
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Amount Applied
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Deleted?
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {linkedPayments.map((row) => {
                                const p = row.payment;
                                const statusBadge =
                                  p?.status === 'Paid'
                                    ? 'bg-green-100 text-green-800'
                                    : p?.status === 'Pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : p?.status === 'Refunded'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800';
                                return (
                                  <tr key={row.payment_id}>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">
                                        {p?.invoice_number || '-'}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">
                                        {p?.payment_date
                                          ? new Date(p.payment_date).toLocaleDateString('en-GB', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                            })
                                          : 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{p?.payment_method || '-'}</div>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          p?.payment_type === 'Full'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-indigo-100 text-indigo-800'
                                        }`}
                                      >
                                        {p?.payment_type || '-'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge}`}>
                                        {p?.status || '-'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                      <div className="text-sm text-gray-900">
                                        {p?.amount != null ? `RM${Number(p.amount).toFixed(2)}` : '-'}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                      <div className="text-sm text-gray-900">
                                        {row.amount_applied != null ? `RM${Number(row.amount_applied).toFixed(2)}` : '-'}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          p?.is_deleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                        }`}
                                      >
                                        {p?.is_deleted ? 'Yes' : 'No'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-right">
                                      {p?.id ? (
                                        <Link to={`/payments/${p.id}`} className="text-blue-600 hover:text-blue-800 text-sm">
                                          View
                                        </Link>
                                      ) : (
                                        <span className="text-sm text-gray-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Drawer footer actions */}
                <div className="p-4 border-t flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDrawerOpen(false)}>
                    Close
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!drawerRow?.is_deleted || restoreMutation.isPending}
                    onClick={() => onRestore(drawerRow)}
                  >
                    {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600"
                    disabled={!drawerRow?.is_deleted || hardDeleteMutation.isPending}
                    onClick={() => onHardDelete(drawerRow)}
                  >
                    {hardDeleteMutation.isPending ? 'Deleting…' : 'Hard Delete'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="p-4 text-sm text-gray-500">No enrollment selected.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
